import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Upload, X, ShieldCheck } from "lucide-react";
import AudioRecorder from "@/components/AudioRecorder";
import { RELATIONS } from "@/lib/relations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TributeModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", relation: "", message: "" });
  const [otherRelation, setOtherRelation] = useState("");
  const [kind, setKind] = useState("text");
  const [lang, setLang] = useState("Tori");
  const [audioFile, setAudioFile] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recKey, setRecKey] = useState(0);
  const [busy, setBusy] = useState(false);

  const field =
    "w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-800 placeholder:text-stone-400 focus:border-[#C5A059] focus:outline-none focus:ring-2 focus:ring-[#C5A059]/30";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Veuillez indiquer votre nom.");
      return;
    }
    if (kind === "text" && !form.message.trim()) {
      toast.error("Veuillez écrire votre message.");
      return;
    }
    if (kind === "audio" && !recordedBlob && !audioFile) {
      toast.error("Veuillez enregistrer ou choisir un fichier audio.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append(
        "relation",
        form.relation === "Toute autre personne" && otherRelation.trim()
          ? otherRelation.trim()
          : form.relation
      );
      fd.append("kind", kind);
      if (kind === "text") {
        fd.append("message", form.message.trim());
      } else {
        fd.append("lang", lang);
        if (recordedBlob) {
          fd.append("audio", recordedBlob, "hommage-vocal.webm");
        } else {
          fd.append("audio", audioFile);
        }
      }
      await axios.post(`${API}/messages`, fd);
      setForm({ name: "", relation: "", message: "" });
      setOtherRelation("");
      setAudioFile(null);
      setRecordedBlob(null);
      setRecKey((k) => k + 1);
      toast.success("Hommage envoyé — il sera publié après validation par la famille.");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "L'hommage n'a pas pu être envoyé.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="tribute-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-[#FAF8F5] p-6 sm:max-w-lg sm:rounded-3xl sm:p-8"
          >
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-display text-2xl font-semibold text-stone-900">
                Déposer un hommage
              </h3>
              <button
                data-testid="tribute-modal-close"
                onClick={onClose}
                className="rounded-full border border-stone-300 p-2 text-stone-500 transition-colors hover:border-[#C85A32] hover:text-[#C85A32]"
                aria-label="Fermer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div
              data-testid="tribute-moderation-notice"
              className="mb-5 flex items-start gap-3 rounded-2xl border border-[#C5A059]/40 bg-[#C5A059]/10 p-4"
            >
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#8a6b2e]" />
              <p className="text-xs leading-relaxed text-stone-600">
                <span className="font-semibold text-stone-800">
                  Validation avant publication :
                </span>{" "}
                votre hommage sera relu et validé par la famille avant
                d'apparaître sur le site.
              </p>
            </div>

            <form data-testid="guestbook-form" onSubmit={submit} className="space-y-4">
              <input
                data-testid="guestbook-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Votre Nom / Prénom"
                maxLength={120}
                className={field}
              />
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-400">
                  Je suis pour le défunt
                </label>
                <select
                  data-testid="guestbook-relation-input"
                  value={form.relation}
                  onChange={(e) => setForm({ ...form, relation: e.target.value })}
                  className={field}
                >
                  <option value="">Choisir…</option>
                  {RELATIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                {form.relation === "Toute autre personne" && (
                  <input
                    data-testid="guestbook-relation-other"
                    value={otherRelation}
                    onChange={(e) => setOtherRelation(e.target.value)}
                    placeholder="Précisez votre relation (ex. parrain, voisin…)"
                    maxLength={120}
                    className={`${field} mt-3`}
                  />
                )}
              </div>

              <div className="flex rounded-full border border-stone-200 bg-[#F4EFEA] p-1">
                <button
                  type="button"
                  data-testid="guestbook-kind-text"
                  onClick={() => setKind("text")}
                  className={`flex-1 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-300 ${
                    kind === "text"
                      ? "bg-[#1C1917] text-[#FAF8F5]"
                      : "text-stone-500 hover:text-stone-800"
                  }`}
                >
                  Texte
                </button>
                <button
                  type="button"
                  data-testid="guestbook-kind-audio"
                  onClick={() => setKind("audio")}
                  className={`flex-1 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-300 ${
                    kind === "audio"
                      ? "bg-[#1C1917] text-[#FAF8F5]"
                      : "text-stone-500 hover:text-stone-800"
                  }`}
                >
                  Audio
                </button>
              </div>

              {kind === "text" ? (
                <div>
                  <textarea
                    data-testid="guestbook-message-input"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Votre message de condoléances ou souvenir"
                    rows={4}
                    maxLength={300}
                    className={`${field} resize-none`}
                  />
                  <p
                    data-testid="guestbook-char-counter"
                    className="mt-1 text-right text-[11px] text-stone-400"
                  >
                    {form.message.length}/300 caractères
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <select
                    data-testid="guestbook-lang-select"
                    value={lang}
                    onChange={(e) => setLang(e.target.value)}
                    className={field}
                  >
                    <option value="Tori">Langue de l'audio : Tori</option>
                    <option value="Goun">Langue de l'audio : Goun</option>
                    <option value="Français">Langue de l'audio : Français</option>
                  </select>
                  <AudioRecorder key={recKey} onChange={setRecordedBlob} />
                  <p className="text-center text-[11px] text-stone-400">
                    ou joignez un fichier
                  </p>
                  <label
                    data-testid="guestbook-audio-drop"
                    className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-stone-300 bg-white px-4 py-4 text-sm text-stone-500 transition-colors hover:border-[#C5A059]"
                  >
                    <Upload className="h-4 w-4 text-[#C5A059]" />
                    {audioFile ? audioFile.name : "Choisir un fichier audio (15 Mo max)"}
                    <input
                      data-testid="guestbook-audio-input"
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                    />
                  </label>
                </div>
              )}

              <button
                data-testid="guestbook-submit-button"
                type="submit"
                disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#1C1917] px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#FAF8F5] transition-colors duration-300 hover:bg-[#C85A32] disabled:opacity-60"
              >
                <Send className="h-4 w-4" />
                {busy ? "Envoi…" : "Envoyer l'hommage"}
              </button>
              <p className="text-center text-[11px] leading-relaxed text-stone-400">
                Merci pour votre témoignage — il honorera sa mémoire.
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
